import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import BackToSiteBadge from "@/components/site/BackToSiteBadge";

export default function AdminSupport() {
  const [me, setMe] = useState(null);
  const [txnId, setTxnId] = useState("");
  const [lookup, setLookup] = useState(null);
  const [reason, setReason] = useState("");
  const [emailOverride, setEmailOverride] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/admin/me`, { withCredentials: true })
      .then((r) => setMe(r.data.user))
      .catch(() => navigate("/admin/login?next=/support", { replace: true }));
  }, [navigate]);

  const search = async () => {
    setMsg(null);
    setLookup(null);
    const id = txnId.trim();
    if (!id) {
      setMsg({ tone: "error", text: "Enter a Paddle transaction id first." });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/transaction/${encodeURIComponent(id)}`, { withCredentials: true });
      setLookup(r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setMsg({
          tone: "info",
          text: "No transaction with that id in our database yet. If Paddle confirmed the sale but it never fulfilled, click 'Backfill from Paddle' to pull it from the Paddle API.",
        });
        setLookup({ not_found: true, transaction_id: id });
      } else {
        setMsg({ tone: "error", text: e?.response?.data?.detail || "Lookup failed" });
      }
    } finally {
      setBusy(false);
    }
  };

  const backfill = async () => {
    const id = (lookup?.transaction_id || txnId).trim();
    if (!id) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(
        `${API}/admin/backfill-from-paddle`,
        {
          transaction_id: id,
          reason: reason.trim() || null,
          email_override: emailOverride.trim() || null,
        },
        { withCredentials: true }
      );
      setMsg({
        tone: "ok",
        text: `Pulled from Paddle. Buyer ${r.data.email} will receive the PDF within ~5 seconds.`,
      });
      setTimeout(search, 4000);
    } catch (e) {
      setMsg({
        tone: "error",
        text: e?.response?.data?.detail || "Backfill failed. Check that PADDLE_API_KEY is set in Secrets.",
      });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!lookup?.transaction?.transaction_id) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(
        `${API}/admin/rescue-fulfilment`,
        {
          transaction_id: lookup.transaction.transaction_id,
          force,
          reason: reason.trim() || null,
        },
        { withCredentials: true }
      );
      if (r.data?.already_sent) {
        setMsg({ tone: "info", text: `Already delivered to ${r.data.email}. Tick "force" to resend anyway.` });
      } else {
        setMsg({ tone: "ok", text: `Fulfilment queued. ${r.data.email} should receive the PDF link within ~5 seconds.` });
      }
      // Re-fetch after a short delay so the UI shows the new state
      setTimeout(search, 4000);
    } catch (e) {
      setMsg({ tone: "error", text: e?.response?.data?.detail || "Resend failed" });
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <div className="min-h-screen bg-[var(--cream)]" />;

  const tx = lookup?.transaction;
  const ful = lookup?.fulfilment;

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="px-6 md:px-10 py-5 border-b border-[rgba(15,31,23,0.08)] flex items-center justify-between gap-3">
        <BackToSiteBadge />
        <div className="hidden md:flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center text-xs font-bold">N</div>
          <div>
            <div className="font-display text-lg leading-none">Support · Order rescue</div>
            <div className="text-[11px] text-[var(--muted)] mt-1 font-mono-caption tracking-[0.2em] uppercase">Milk2Income · Admin</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/admin" className="px-4 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-sm" data-testid="support-back-admin">
            ← Admin dashboard
          </Link>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-3xl mx-auto space-y-8">
        <section>
          <h1 className="font-display text-3xl md:text-4xl mb-3">Resend a buyer's PDF</h1>
          <p className="text-sm text-[var(--muted)] max-w-xl">
            Paste the Paddle transaction id (starts with <code className="font-mono text-[var(--ink)]">txn_</code>) from the Paddle dashboard. We'll show you the current state and let you queue a fresh PDF + email in one click.
          </p>
        </section>

        <section className="p-5 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
          <label className="block text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
            Paddle transaction id
          </label>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              className="input-base flex-1 font-mono"
              value={txnId}
              onChange={(e) => setTxnId(e.target.value)}
              placeholder="txn_01hxyz…"
              onKeyDown={(e) => e.key === "Enter" && search()}
              data-testid="support-txn-input"
            />
            <button
              onClick={search}
              disabled={busy}
              className="px-6 py-3 rounded-full bg-[var(--ink)] text-[var(--cream)] text-sm font-medium disabled:opacity-60"
              data-testid="support-lookup-btn"
            >
              {busy ? "Looking up…" : "Look up"}
            </button>
          </div>
        </section>

        {msg && (
          <div
            className={`p-4 rounded-xl text-sm ${
              msg.tone === "error"
                ? "bg-red-50 text-red-700"
                : msg.tone === "ok"
                ? "bg-[var(--jade)]/10 text-[var(--jade)]"
                : "bg-[var(--marigold)]/10 text-[var(--marigold)]"
            }`}
            data-testid="support-msg"
          >
            {msg.text}
          </div>
        )}

        {lookup?.not_found && (
          <section className="p-5 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)] space-y-4" data-testid="support-backfill-section">
            <div>
              <h2 className="font-display text-xl mb-1">Not in our database yet</h2>
              <p className="text-sm text-[var(--muted)]">
                Pull the full transaction from Paddle's API using <code className="font-mono text-[var(--ink)]">PADDLE_API_KEY</code>, then watermark + email the buyer.
              </p>
            </div>

            <label className="block text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
              Buyer email (optional override)
            </label>
            <input
              type="email"
              className="input-base"
              placeholder="buyer@example.com — leave blank if Paddle already stores it"
              value={emailOverride}
              onChange={(e) => setEmailOverride(e.target.value)}
              data-testid="support-backfill-email-override"
            />
            <p className="text-xs text-[var(--muted)] -mt-2">
              Paste the buyer's email if your Paddle API key can't read Customers (403) or the webhook stripped PII. We'll trust this value and email the PDF there.
            </p>

            <label className="block text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
              Reason (optional, logged in audit trail)
            </label>
            <input
              className="input-base"
              placeholder="e.g. Live webhook landed before deploy of email-resolution fix"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="support-backfill-reason"
            />

            <button
              onClick={backfill}
              disabled={busy}
              className="mt-2 px-6 py-3 rounded-full bg-[var(--jade)] text-white font-medium disabled:opacity-60"
              data-testid="support-backfill-btn"
            >
              {busy ? "Pulling from Paddle…" : "Backfill from Paddle"}
            </button>
            <p className="text-xs text-[var(--muted)] mt-2">
              This calls Paddle's API, resolves the buyer email, creates the transaction row, generates the watermarked PDF and emails the buyer — all in one.
            </p>
          </section>
        )}

        {tx && (
          <section className="p-5 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)] space-y-4">
            <div>
              <h2 className="font-display text-xl mb-1">Order found</h2>
              <p className="text-xs font-mono text-[var(--muted)]" data-testid="support-txn-id">
                {tx.transaction_id} · {tx.order_id}
              </p>
            </div>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Row label="Buyer email" value={tx.email} testid="support-email" />
              <Row label="Buyer name" value={tx.name || "—"} />
              <Row label="Phone" value={tx.phone || "—"} />
              <Row label="Locale" value={tx.locale} />
              <Row label="Transaction date" value={tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"} />
              <Row
                label="Fulfilment state"
                value={
                  <StatePill state={ful?.state || "none"} />
                }
                testid="support-state"
              />
              {ful?.email_sent !== undefined && (
                <Row label="Last email delivered" value={ful.email_sent ? "Yes ✓" : "No — blocked or failed"} />
              )}
              {ful?.download_url && (
                <Row
                  label="Current download link"
                  value={
                    <a
                      href={ful.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-[var(--marigold)] break-all"
                      data-testid="support-download-link"
                    >
                      {ful.download_url}
                    </a>
                  }
                />
              )}
            </dl>

            <div className="pt-3 border-t border-[rgba(15,31,23,0.08)]">
              <label className="block text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
                Reason (optional, logged in audit trail)
              </label>
              <input
                className="input-base"
                placeholder="e.g. Buyer never received the email"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="support-reason"
              />

              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  data-testid="support-force"
                />
                Force resend even if already delivered
              </label>

              <button
                onClick={resend}
                disabled={busy}
                className="mt-4 px-6 py-3 rounded-full bg-[var(--marigold)] text-white font-medium disabled:opacity-60"
                data-testid="support-resend-btn"
              >
                {busy ? "Queuing…" : "Resend PDF now"}
              </button>
              <p className="text-xs text-[var(--muted)] mt-2">
                Regenerates the watermarked PDF and emails the buyer again. The action is audited.
              </p>
            </div>
          </section>
        )}

        <section className="p-4 rounded-xl bg-[var(--marigold)]/5 border border-[var(--marigold)]/20 text-sm">
          <strong className="text-[var(--marigold)]">How to find a Paddle transaction id:</strong> In your Paddle dashboard open{" "}
          <em>Transactions</em>, pick the sale, and copy the id at the top (starts with{" "}
          <code className="font-mono">txn_</code>). You can also pull it from the owner-sale-alert email you receive on every purchase.
        </section>
      </main>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="p-3 rounded-xl bg-[var(--cream-2)]/60">
      <div className="text-[11px] font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-1">
        {label}
      </div>
      <div className="text-sm break-words" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function StatePill({ state }) {
  const map = {
    sent: "bg-[var(--jade)]/15 text-[var(--jade)]",
    email_failed: "bg-red-100 text-red-700",
    processing: "bg-[var(--marigold)]/15 text-[var(--marigold)]",
    failed: "bg-red-100 text-red-700",
    none: "bg-gray-200 text-gray-700",
  };
  const label =
    state === "sent"
      ? "Delivered"
      : state === "email_failed"
      ? "Email failed"
      : state === "processing"
      ? "Processing"
      : state === "failed"
      ? "Fulfilment failed"
      : "Not fulfilled yet";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-mono-caption tracking-[0.1em] uppercase ${
        map[state] || "bg-gray-100 text-gray-700"
      }`}
    >
      {label}
    </span>
  );
}
